// LinkAssistPopover — a reusable "ask Link to help with this text" affordance.
//
// A small Link button that opens a card of AI helper actions (rewrite tone,
// shorten, proofread) plus a free-form instruction box. It runs the supplied
// `run(action, instruction)` — the caller injects the actual API call, so this
// is domain-agnostic (needs description, course description, step bodies…).
// Result is shown as a preview the user applies or discards (preview-then-apply,
// never silently overwrites). This is THE standardised Link-on-text affordance —
// no per-surface special cases.

import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Loader2, Check, RotateCcw, Wand2, Feather, Scissors, SpellCheck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { LinkAvatar } from '@/components/icons/LinkAvatar';

export interface LinkAssistAction {
    key: string;
    label: string;
    icon?: LucideIcon;
}

// Sensible defaults for revising a body of prose. Keys match the backend's
// NeedAssistAction values so callers can pass them straight through.
export const DEFAULT_ASSIST_ACTIONS: LinkAssistAction[] = [
    { key: 'rewrite_formal', label: 'Mer formell ton', icon: Wand2 },
    { key: 'rewrite_engaging', label: 'Mer engagerande', icon: Feather },
    { key: 'shorten', label: 'Korta ner', icon: Scissors },
    { key: 'proofread', label: 'Rätta språket', icon: SpellCheck },
];

interface LinkAssistPopoverProps {
    /** Perform the assist. Return the revised text (markdown). Throw to error. */
    run: (action: string, instruction: string) => Promise<string>;
    /** Apply the accepted revision. */
    onApply: (revised: string) => void;
    actions?: LinkAssistAction[];
    disabled?: boolean;
    /** Free-form box action key + placeholder. */
    customActionKey?: string;
    customPlaceholder?: string;
    align?: 'left' | 'right';
    /**
     * The text Link will act on — shown truncated so the user sees exactly what
     * the action targets (the whole body for a toolbar button, the selection for
     * a bubble-menu button). May be a getter so callers can read it lazily (e.g.
     * the live editor selection when the popover opens).
     */
    subjectText?: string | (() => string);
}

// First few words of the subject, for the "acts on" hint. Kept short on purpose.
const truncateSubject = (raw: string): string => {
    const s = raw.replace(/\s+/g, ' ').trim();
    if (!s) return '';
    const words = s.split(' ');
    const head = words.slice(0, 4).join(' ');
    return head.length < s.length ? `${head}…` : head;
};

export const LinkAssistPopover: React.FC<LinkAssistPopoverProps> = ({
    run,
    onApply,
    actions = DEFAULT_ASSIST_ACTIONS,
    disabled,
    customActionKey = 'custom',
    customPlaceholder = 'Be Link om något — t.ex. "utveckla avsnittet om förmåner"',
    align = 'right',
    subjectText,
}) => {
    const [open, setOpen] = useState(false);
    const [instruction, setInstruction] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<string | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const close = () => {
        setOpen(false);
        setResult(null);
        setError(null);
        setBusy(false);
    };

    const exec = async (action: string, extra: string) => {
        setBusy(true);
        setError(null);
        setResult(null);
        try {
            const revised = await run(action, extra);
            setResult(revised);
        } catch (e: any) {
            setError(e?.message || 'Link kunde inte hjälpa till just nu. Försök igen.');
        } finally {
            setBusy(false);
        }
    };

    const apply = () => {
        if (result != null) onApply(result);
        close();
    };

    const subject = truncateSubject(typeof subjectText === 'function' ? subjectText() : (subjectText ?? ''));

    return (
        <div ref={rootRef} className="relative inline-flex">
            <button
                type="button"
                disabled={disabled}
                onClick={() => (open ? close() : setOpen(true))}
                title="Fråga Link"
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors disabled:opacity-40 ${
                    open ? 'bg-[#A4C3B2]/30 text-[#1A4D4F]' : 'text-[#1A4D4F] hover:bg-[#A4C3B2]/20'
                }`}
            >
                <LinkAvatar className="w-4 h-4" /> Fråga Link
            </button>

            {open && (
                <div
                    className={`absolute top-full mt-1.5 z-50 w-80 rounded-xl border border-gray-200 bg-white shadow-xl p-3 ${
                        align === 'right' ? 'right-0' : 'left-0'
                    }`}
                >
                    {busy ? (
                        <div className="flex items-center gap-2 py-6 justify-center text-sm text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin" /> Link arbetar…
                        </div>
                    ) : result != null ? (
                        <div className="space-y-2.5">
                            <p className="text-xs font-medium text-gray-500">Link föreslår:</p>
                            <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-100 bg-gray-50 p-2.5 prose prose-sm max-w-none text-gray-700 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1">
                                <ReactMarkdown>{result}</ReactMarkdown>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={apply}
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1A4D4F] text-white text-xs font-medium hover:bg-[#143a3c]"
                                >
                                    <Check className="w-3.5 h-3.5" /> Använd
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setResult(null)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs font-medium hover:bg-gray-50"
                                >
                                    <RotateCcw className="w-3.5 h-3.5" /> Ändra
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-2.5">
                            {subject && (
                                <p className="text-[11px] text-gray-400 truncate">
                                    Link agerar på: <span className="text-gray-600 font-medium">"{subject}"</span>
                                </p>
                            )}
                            <div className="flex flex-wrap gap-1.5">
                                {actions.map((a) => {
                                    const Icon = a.icon;
                                    return (
                                        <button
                                            key={a.key}
                                            type="button"
                                            onClick={() => exec(a.key, '')}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:border-[#1A4D4F] hover:text-[#1A4D4F] transition-colors"
                                        >
                                            {Icon && <Icon className="w-3.5 h-3.5" />} {a.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="pt-0.5">
                                <textarea
                                    value={instruction}
                                    onChange={(e) => setInstruction(e.target.value)}
                                    placeholder={customPlaceholder}
                                    rows={2}
                                    className="w-full p-2 text-sm bg-white border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#1A4D4F]"
                                />
                                <button
                                    type="button"
                                    onClick={() => exec(customActionKey, instruction.trim())}
                                    disabled={!instruction.trim()}
                                    className="mt-1.5 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1A4D4F] text-white text-xs font-medium hover:bg-[#143a3c] disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <LinkAvatar className="w-3.5 h-3.5" bg="transparent" fg="#FFFFFF" /> Fråga Link
                                </button>
                            </div>
                            {error && <p className="text-xs text-red-600">{error}</p>}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LinkAssistPopover;
